import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../../utils/axios";
import {
  Layers,
  Shield,
  CheckCircle2,
  Loader2,
  Users,
  Briefcase,
  Store,
  User,
} from "lucide-react";

interface RoleItem {
  id: number;
  name: string;
  description: string;
}

interface PermissionItem {
  id: number;
  name: string;
  module: string;
  action: string;
  description: string;
}

const HIERARCHY_DETAILS = [
  {
    role: "SUPER_ADMIN",
    title: "Super Administrator",
    level: "Tier 1 (Highest)",
    desc: "Complete unrestricted access across all system databases, finance, settings, admins, and operations. Bypasses normal role restrictions.",
    color: "amber",
    icon: Shield,
  },
  {
    role: "ADMIN",
    title: "Administrator",
    level: "Tier 2",
    desc: "Manages Back Office staff, Agents & Stores, Players, Finances, Bonuses, and Reports according to assigned permissions.",
    color: "indigo",
    icon: Users,
  },
  {
    role: "BACK_OFFICE",
    title: "Back Office Staff",
    level: "Tier 3",
    desc: "Operational team handling day-to-day player KYC, recharge approvals, withdrawal processing, and transactional reporting.",
    color: "emerald",
    icon: Briefcase,
  },
  {
    role: "AGENT",
    title: "Agent / Store Collaborator",
    level: "Tier 4",
    desc: "Unique QR code and referral link owner. Can ONLY view their own assigned players, earnings, commissions, and agent wallet.",
    color: "blue",
    icon: Store,
  },
  {
    role: "USER",
    title: "Player / User",
    level: "Tier 5 (Base)",
    desc: "End-user player. Access restricted exclusively to their own account balance, games, bets, bonuses, and deposits/withdrawals.",
    color: "gray",
    icon: User,
  },
];

const RolePermissions: React.FC = () => {
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [groupedPermissions, setGroupedPermissions] = useState<Record<string, PermissionItem[]>>({});
  const [loading, setLoading] = useState(true);

  // Active Role Tab for preview
  const [selectedRole, setSelectedRole] = useState("ADMIN");
  const [rolePerms, setRolePerms] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      fetchRolePerms(selectedRole);
    }
  }, [selectedRole]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get("/roles"),
        api.get("/permissions"),
      ]);

      if (rolesRes.data?.success) {
        setRoles(rolesRes.data.data || []);
      }

      if (permsRes.data?.success) {
        const perms = permsRes.data.data?.permissions || [];
        setPermissions(perms);
        setGroupedPermissions(permsRes.data.data?.grouped || {});
      }
    } catch (err: any) {
      console.error(err);
      toast.error("Failed to load roles and permissions");
    } finally {
      setLoading(false);
    }
  };

  const fetchRolePerms = async (roleName: string) => {
    try {
      const res = await api.get(`/role-permissions/${roleName}`);
      if (res.data?.success) {
        const ids = (res.data.data.permissions || []).map((p: any) => p.id);
        setRolePerms(ids);
      }
    } catch (err: any) {
      console.error(err);
    }
  };

  const togglePerm = (pId: number) => {
    if (selectedRole === "SUPER_ADMIN") return;
    setRolePerms((prev) =>
      prev.includes(pId) ? prev.filter((id) => id !== pId) : [...prev, pId]
    );
  };

  const handleSaveRolePermissions = async () => {
    if (selectedRole === "SUPER_ADMIN") return;
    setSubmitting(true);
    try {
      const res = await api.put(`/role-permissions/${selectedRole}`, {
        permissionIds: rolePerms,
      });
      if (res.data?.success) {
        toast.success(`Default permissions updated for ${selectedRole}`);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update role permissions");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className=" space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-3">
        <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
          <Layers size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Role & Access Control System</h1>
          <p className="text-sm text-gray-500">
            Overview of the 5-tier role hierarchy and module permissions.
          </p>
        </div>
      </div>

      {/* Hierarchy Cards */}
      <div className="space-y-3">
        <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <span>Hierarchy Flow</span>
          <span className="text-xs font-normal text-gray-400">
            (Superior roles inherit administrative oversight)
          </span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {HIERARCHY_DETAILS.map((h, i) => {
            const Icon = h.icon;
            return (
              <div
                key={h.role}
                onClick={() => setSelectedRole(h.role)}
                className={`p-4 rounded-2xl border transition cursor-pointer flex flex-col justify-between ${selectedRole === h.role
                  ? "bg-indigo-50/50 border-indigo-300 shadow-sm"
                  : "bg-white border-gray-100 hover:border-gray-200"
                  }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold tracking-wider uppercase text-gray-400">
                      {h.level}
                    </span>
                    <Icon size={18} className="text-gray-500" />
                  </div>
                  <h3 className="font-bold text-sm text-gray-900 mb-1">{h.title}</h3>
                  <span className="inline-block font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 mb-2">
                    {h.role}
                  </span>
                  <p className="text-xs text-gray-500 leading-relaxed">{h.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Permissions Matrix */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-500">Configuring Role:</span>
              <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-100 text-indigo-700 font-mono">
                {selectedRole}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {selectedRole === "SUPER_ADMIN"
                ? "Super Admin unconditionally possesses all permissions (*)."
                : `Toggle default permissions assigned to ${selectedRole} accounts.`}
            </p>
          </div>

          {selectedRole !== "SUPER_ADMIN" && (
            <button
              onClick={handleSaveRolePermissions}
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-xl text-sm font-semibold transition flex items-center gap-2 shadow-sm"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              Save Role Defaults
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400">
            <Loader2 className="animate-spin inline-block mr-2" size={20} />
            Loading permissions matrix...
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedPermissions).map(([moduleName, perms]) => (
              <div key={moduleName} className="space-y-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    {moduleName} Module
                  </h3>
                  <div className="flex-1 h-px bg-gray-100" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {perms.map((p) => {
                    const isGranted =
                      selectedRole === "SUPER_ADMIN" || rolePerms.includes(p.id);
                    return (
                      <div
                        key={p.id}
                        onClick={() => togglePerm(p.id)}
                        className={`p-3 rounded-xl border text-left transition select-none flex items-start gap-3 ${selectedRole === "SUPER_ADMIN"
                          ? "bg-amber-50/30 border-amber-200 cursor-default"
                          : isGranted
                            ? "bg-indigo-50/50 border-indigo-200 cursor-pointer"
                            : "bg-white border-gray-100 hover:border-gray-200 cursor-pointer"
                          }`}
                      >
                        <div
                          className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center text-xs ${isGranted
                            ? "bg-indigo-600 text-white"
                            : "border border-gray-300 bg-white"
                            }`}
                        >
                          {isGranted && <CheckCircle2 size={13} />}
                        </div>
                        <div className="flex-1">
                          <div className="text-xs font-semibold text-gray-800">{p.name}</div>
                          <div className="text-[11px] text-gray-500 leading-snug mt-0.5">
                            {p.description}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RolePermissions;
